// [목적] /folio/:id — 좌측 일정 리스트(Day 탭 + 밴드 타임라인 + 예산 카테고리 바) / 우측 가짜 지도
// [이유] W5(design-alignment): 타임라인 마커 01/02→1/2, 아이콘 타입약자→emoji(R-4 폴백),
//         BudgetBar → 카테고리 색상 바+도트 범례(folio.jsx 시안 정합). PlanItem.icon 정식 타입 사용(fe-be-gap-tier2 T13).
//         fe-be-gap-tier2 T14: BudgetCategoryBar → folio.budgetBreakdown 실데이터 우선, 부재 시 BUDGET mock 폴백.
//         fe-be-gap-tier2 T15: 작성자 author.handle 우선 표시(@slug 폴백), viewCount 표시.
//         folio-generation-quality T13: day 아이템을 4밴드(오전/점심/오후/저녁) 그룹 타임라인으로 렌더.
//         folio-generation-quality T15: BudgetCategoryBar 0원 숨김·인당 약 N만원·만원 표기 정합(ADR-FGQ-OQ5/6).
//         folio-generation-quality gap: groupPlanItemsByBand 로컬 헬퍼 → timeBand.groupPlanItemsByTimeBand 통일.
//           planItemSchema.timeOfDay 추가로 타입 캐스팅 없이 item.timeOfDay 우선 사용 가능.
//         folio-enrichment WC-ENRICH-15: PlanItemEnrichedContent 주입 — activity/tip/factInfo/firstImage 풍부화 렌더.
//         fe-folio-social-wire: 좋아요/북마크 버튼(LikeButton/BookmarkButton) + 댓글 섹션(CommentSection)을
//           folio-social.client.ts 에 최초 배선. 지금까지 이 클라이언트는 export 만 되고 호출부가 0곳이었음.
// [사이드 이팩트] groupPlanItemsByBand 로컬 헬퍼 제거 → groupPlanItemsByTimeBand import 로 교체.
//                 BAND_META·BAND_ORDER·groupPlanItemsByTimeBand import 추가(groupItemsByBand 미사용으로 제거).
//                 formatKrw(FolioBudgetPreview) import — 예산 포맷 단일 진입점.
//                 FolioMobile 도 동일 PLAN_ITEM_EMOJI_MAP 를 재사용(W5-3 동일 파일 내 매핑).
//                 BUDGET_CATEGORY_COLORS: 예산 카테고리 색상을 design 토큰으로 매핑(hex 하드코딩 금지).
//                 PlanItemEnrichedContent import 추가 — activity/tip/factInfo/firstImage 렌더.
//                 useFolioLike/useFolioBookmark/useAuthGate/LoginPromptModal/CommentSection import 추가.
// [수정 시 고려사항] PlanItem.icon: planItemSchema.icon(z.string().nullable().optional()) 정식 타입 — 단언 제거됨.
//                   PlanItem.timeOfDay: planItemSchema.timeOfDay 정식 추가 — 타입 캐스팅 불필요.
//                   budgetBreakdown: folio.budgetBreakdown(nullable) 실데이터 → 카테고리 라벨 한글 매핑 포함.
//                   WC-TOURAPI-QR-5-02/05 — "조용한" Quick Chip + VITE_FEATURE_QUIET_CHIP feature flag 추가.
//                   WC-TOURAPI-QR-5-03 — CongestionForecastCard 를 POI 상세 카드 하단에 부착 (poiId 연동 후속).
//                   PlanItemEnrichedContent: compact=false(뷰어) — 전체 필드 표시. graceful: 전부 null 이면 미표시.
//                   [G2-1, 2026-09-17] 계약 C1(folio.viewer) 반영 완료 — LikeButton/BookmarkButton 이
//                   folio.viewer?.liked/bookmarked 를 초기값으로 시드해 새로고침 후에도 상태 유지.
//         FlowLine(2026-09-17) A1/A2/A3/A4/A6/A7/A8/A9 — 공유/PDF/예산실데이터/지도 SDK/날씨 강수확률/
//           신고/Day 가져오기/소유자 전용 편집 버튼을 이 페이지에 배선. 상세 근거는 각 블록 주석 참조.

import {
  Alert,
  Box,
  ButtonBase,
  CircularProgress,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { DayWeather, Folio, PlanItem } from '@gangwoncanvas/types';
import { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CongestionForecastCard } from '../components/folio/CongestionForecastCard.js';
import { PlanItemEnrichedContent } from '../components/folio/PlanItemEnrichedContent.js';
import { FolioCommentSection } from '../components/folio/CommentSection.js';
import { DayImportDialog } from '../components/folio/DayImportDialog.js';
import { FolioExportButton } from '../components/folio/FolioExportButton.js';
import { FolioMap } from '../components/folio/FolioMap.js';
import { FolioShareButton } from '../components/folio/FolioShareButton.js';
import { formatKrw } from '../components/folio/preview/FolioBudgetPreview.js';
import {
  groupPlanItemsByTimeBand,
  BAND_META,
  formatTimeRange,
  formatForecastDate,
} from '../components/folio/timeBand.js';
import { LineIcon } from '../components/icons/LineIcons.js';
import { ReportDialog } from '../components/report/ReportDialog.js';
import { TopBar } from '../components/layout/TopBar.js';
import { LoginPromptModal } from '../components/auth/LoginPromptModal.js';
import { useAuth } from '../hooks/useAuth.js';
import { useAuthGate } from '../hooks/useAuthGate.js';
import { useFolio } from '../hooks/useFolio.js';
import { useForkFolio } from '../hooks/useForkFolio.js';
import { useFolioLike } from '../hooks/useFolioLike.js';
import { useFolioBookmark } from '../hooks/useFolioBookmark.js';
import { usePoiCongestion } from '../hooks/usePoiCongestion.js';
import { useShell } from '../hooks/useShell.js';
import { distributePercentages } from '../utils/distributePercentages.js';
import { folioAuthorLabel } from '../utils/folioAuthorLabel.js';

import { FolioMobile } from './mobile/FolioMobile.js';

// [목적] 스크린리더 전용 텍스트(시각적으로는 숨김) 공용 스타일
// [이유] 2026-09-20 조회수 글리프를 이모지('👁')에서 라인 아이콘으로 교체하면서, 아이콘만으로는
//        보조기술에 "조회수"라는 의미가 전달되지 않는다(아이콘은 aria-hidden). 숫자 앞에 숨은 라벨을 둔다.
// [수정 시 고려사항] clip-path 대신 clip+1px 박스 관용구를 쓰는 이유는 구형 사파리 호환 — 다른 곳에서도
//                   필요해지면 components/ 공용 스타일로 승격할 것.
const visuallyHiddenSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const BORDER_SOFT = c.line.soft[L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const ACCENT = c.accent[500][L];

// 토큰 단축 참조 — 실제 토큰 구조: radius={s,m,l,xl}, shadow={card,modal}, motion={fast,med,slow}
const R = tokens.radius; // s=6, m=10, l=16, xl=24
// SH(tokens.shadow) 는 MapView(순번 가짜 좌표 SVG placeholder) 제거(A4, FolioMap.tsx 로 대체)로 미사용 — 제거.
const SP = tokens.spacing; // 0,1,2,3,4,5,6,8,10,12,16 (4단위)
const MO = tokens.motion; // fast.duration, med.duration, slow.duration

// [목적] "조용한" 칩 전용 색상 토큰 참조 (FolioPage 헤더 영역)
// [이유] WC-TOURAPI-QR-5-01/02 — chip.quiet 토큰으로 Fork/공유 버튼과 시각 구분
// [사이드 이팩트] 없음
// [수정 시 고려사항] 디자이너 R-8 검수 후 color.ts 토큰만 갱신하면 자동 반영
//                   [2026-09-18] QUIET_FG 추가 — 기존에 QUIET_BORDER(테두리 전용 설계값)를 비활성
//                   텍스트 색으로 재사용해 bg 위 3.3:1 로 AA 미달이었다. 텍스트는 QUIET_FG(6.55:1) 사용.
const QUIET_BG = c.chip.quiet.bg[L];
const QUIET_BORDER = c.chip.quiet.border[L];
const QUIET_FG = c.chip.quiet.fg[L];
const QUIET_BG_ACTIVE = c.chip.quiet.bgActive[L];
const QUIET_FG_ACTIVE = c.chip.quiet.fgActive[L];

// [목적] 폴리오 헤더 보조 액션 바의 3단 위계 공통 스타일
// [이유] 2026-09-20 디자인 QA: 좋아요/북마크/댓글수/조용한/공유/PDF/Day가져오기/신고 8개가
//        전부 동일한 아웃라인 버튼으로 나열돼 어느 것이 주요 행동인지 구분되지 않았고, 폭을 넘겨
//        "신고"가 혼자 다음 줄로 떨어졌다. 상태 표시(반응) / 도구 / 파괴적 행동(신고) 세 단계로 나눠
//        동일 높이(30px)로 맞추고 크롬(px)을 줄여 한 줄에 들어가게 한다.
// [사이드 이팩트] FolioShareButton/FolioExportButton 은 sx prop 으로 주입 — 두 컴포넌트 모두 sx 병합 지원.
// [수정 시 고려사항] 버튼을 더 추가할 거면 폭을 다시 계산할 것 — 본문 컬럼은 460px 고정이라 여유가 적다.
const ACTION_H = 30;
/** 도구 버튼(공유/PDF/편집/Day 가져오기/조용한) — 아웃라인 */
const actionOutlineSx = {
  height: ACTION_H,
  bgcolor: c.paper[0][L],
  color: INK_HI,
  border: `1px solid ${BORDER}`,
  borderRadius: `${R.s}px`,
  px: `${SP[2]}px`,
  py: 0,
  fontSize: 11.5,
  fontWeight: 600,
  gap: `${SP[1]}px`,
  flexShrink: 0,
  transition: `background-color ${MO.fast.duration}ms, border-color ${MO.fast.duration}ms`,
  '&:hover': { bgcolor: PAPER2 },
  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
};
/** 반응 버튼(좋아요/북마크/댓글수) — 비활성 상태는 테두리 없는 고스트 */
const actionGhostSx = {
  display: 'flex',
  alignItems: 'center',
  height: ACTION_H,
  gap: `${SP[1]}px`,
  borderRadius: `${R.s}px`,
  px: `${SP[2]}px`,
  py: 0,
  fontSize: 11.5,
  fontWeight: 700,
  flexShrink: 0,
  transition: `background-color ${MO.fast.duration}ms, color ${MO.fast.duration}ms`,
  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
};

// [목적] PlanItem.type → emoji 로컬 매핑 테이블 (W5-1, R-4 폴백 전략)
// [이유] PlanItem 스키마에 icon 필드가 부재(planItemKindEnum: place/meal/stay/transit/activity/note).
//         BE 가 icon 필드를 제공할 때까지 FE 로컬 상수로 시각 구현. W1 PreviewMiniCard emoji 규칙과 일관.
// [사이드 이팩트] FolioMobile(W5-3), PreviewMiniCard(W1) 에서도 동일 매핑 전략 사용 — 변경 시 동기 갱신 필요.
// [수정 시 고려사항] PlanItem.icon 필드 추가 시 getItemEmoji 함수에서 item.icon 우선 분기(R-4).
//                   planItemKindEnum 에 새 kind 추가 시 본 상수에도 추가.
export const PLAN_ITEM_EMOJI_MAP: Record<string, string> = {
  place: '📍',
  meal: '🍜',
  stay: '🏨',
  transit: '🚄',
  activity: '🏄',
  note: '📝',
  // 시안 data.js 에서 사용되는 추가 타입 키워드 폴백
  cafe: '☕',
  food: '🍜',
  beach: '🌊',
  sea: '🌊',
  lodging: '🏨',
  transport: '🚄',
  fish: '🐟',
  crab: '🦀',
  surf: '🏄',
  croissant: '🥐',
  taco: '🌮',
};

// [목적] PlanItem 의 emoji 를 결정하는 순수 함수 (W5-1, R-4; fe-be-gap-tier2 T13 정식화)
// [이유] planItemSchema.icon(z.string().nullable().optional()) 정식 타입으로 전환 — 타입 단언 제거(T13).
//         item.icon(정식 BE 필드) 우선, 없거나 null 이면 PLAN_ITEM_EMOJI_MAP[item.type] 폴백, 그것도 없으면 📍.
// [사이드 이팩트] 없음 (순수 함수). FolioMobile 은 이 함수를 import 하므로 자동 반영.
// [수정 시 고려사항] planItemKindEnum 에 새 kind 추가 시 PLAN_ITEM_EMOJI_MAP 에도 추가.
export function getItemEmoji(item: PlanItem): string {
  // T13: icon 은 planItemSchema.icon — z.string().nullable().optional() 정식 타입, 단언 불필요
  if (item.icon) return item.icon;
  return PLAN_ITEM_EMOJI_MAP[item.type] ?? '📍';
}

// [목적] VITE_FEATURE_QUIET_CHIP feature flag 읽기
// [이유] WC-TOURAPI-QR-5-05 R-1 — TourAPI 활용 신청 미완료 대비 런타임 스위치
// [사이드 이팩트] .env 변경 후 Vite dev 서버 재시작 필요
// [수정 시 고려사항] 활용 신청 승인 후 .env 에 VITE_FEATURE_QUIET_CHIP=on 설정 시 즉시 활성화
const QUIET_CHIP_ENABLED = import.meta.env.VITE_FEATURE_QUIET_CHIP === 'on';

// ─── WeatherLabel ─────────────────────────────────────────────────────────────

/**
 * [목적] Day 날씨 인라인 라벨 — weather.code → 아이콘/라벨 로컬 매핑 (fe-be-gap-tier1 T13, ADR-TR-6)
 * [이유] dayPlanSchema.weather({code,tempMin,tempMax,pop}) nullable 직렬화 노출.
 *         날씨 수집 배치 연동 전에는 weather=null 이므로 호출 측에서 null guard 후 렌더.
 *         weather.code → 아이콘/라벨 매핑(로컬 상수). tempMax 있으면 기온 표시.
 *         FlowLine A6(88382b5b): weather.pop(강수확률) 있으면 "강수 N%" 표기 추가.
 * [사이드 이팩트] 없음 — 추가 UI 요소만(레이아웃 회귀 없음).
 * [수정 시 고려사항] 날씨 코드 체계가 기상청 API 코드로 확정되면 매핑 테이블 갱신.
 *                   tempMin/tempMax 모두 null 이면 기온 표시 생략. pop 은 optional — 없으면 미표시(후방호환).
 */
const WEATHER_CODE_MAP: Record<string, { icon: string; label: string }> = {
  sunny: { icon: '☀️', label: '맑음' },
  cloudy: { icon: '⛅', label: '구름 조금' },
  overcast: { icon: '☁️', label: '흐림' },
  rainy: { icon: '🌧', label: '비' },
  snowy: { icon: '❄️', label: '눈' },
  stormy: { icon: '⛈', label: '뇌우' },
  foggy: { icon: '🌫', label: '안개' },
  // 기상청 단기예보 코드 계열 폴백
  '1': { icon: '☀️', label: '맑음' },
  '3': { icon: '⛅', label: '구름 많음' },
  '4': { icon: '☁️', label: '흐림' },
};

function WeatherLabel({ weather }: { weather: DayWeather }): JSX.Element {
  const mapped = weather.code
    ? (WEATHER_CODE_MAP[weather.code] ?? { icon: '🌤', label: weather.code })
    : null;
  const hasTemp = weather.tempMax !== null || weather.tempMin !== null;
  const hasPop = weather.pop != null;
  const dateLabel = formatForecastDate(weather.forecastDate);
  return (
    <Box
      component="span"
      aria-label={`${dateLabel ? `${dateLabel} ` : ''}날씨: ${mapped?.label ?? ''}${hasTemp ? ` 최고 ${weather.tempMax ?? '-'}°C` : ''}${hasPop ? ` 강수확률 ${weather.pop}%` : ''}`}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: `${SP[1]}px`, fontSize: 11 }}
    >
      {/* [목적] 예보 날짜를 "9월 21일(일)" 로 직접 표기
          [이유] [2026-09-21 사용자 요청] '오늘 기준' 이라는 상대 표현보다 실제 월·일이 직관적이다.
                 여행 날짜가 확정되지 않은 폴리오는 오늘부터 N일차로 계산한 날짜가 들어간다.
          [사이드 이팩트] relative=true 면 tooltip 으로 "여행 날짜 미정 → 오늘부터 계산" 사실을 덧붙인다.
          [수정 시 고려사항] forecastDate 는 BE 가 실제 조회에 쓴 날짜 — 표시와 조회가 어긋나지 않는다. */}
      {dateLabel && (
        <Box
          component="span"
          title={
            weather.relative === true
              ? '여행 날짜가 정해지지 않아 오늘부터의 예보를 보여드려요'
              : undefined
          }
          sx={{
            color: INK_HI,
            bgcolor: c.paper[2][L],
            borderRadius: `${R.s}px`,
            px: `${SP[1]}px`,
            fontSize: 10.5,
            fontWeight: 600,
          }}
        >
          {dateLabel}
        </Box>
      )}
      {mapped && (
        <Box component="span" aria-hidden="true">
          {mapped.icon}
        </Box>
      )}
      {mapped && (
        <Box component="span" sx={{ color: INK_MID }}>
          {mapped.label}
        </Box>
      )}
      {hasTemp && (
        <Box
          component="span"
          sx={{
            color: INK_HI,
            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            fontWeight: 600,
          }}
        >
          {weather.tempMax !== null ? `${weather.tempMax}°C` : `${weather.tempMin}°C`}
        </Box>
      )}
      {/* FlowLine A6: 강수확률(pop) — WeatherClient 파생값, 있을 때만 표시(후방호환) */}
      {hasPop && (
        <Box
          component="span"
          sx={{ color: INK_MID, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
        >
          강수 {weather.pop}%
        </Box>
      )}
    </Box>
  );
}

// ─── BudgetCategoryBar ──────────────────────────────────────────────────────

/**
 * [목적] 예산 카테고리 → 디자인 토큰 색상 매핑 (fe-be-gap-tier2 T14)
 * [이유] BudgetCategoryBar 에서 카테고리별 색상을 design-mock 의 hex 가 아닌 design 토큰으로 참조.
 *         CLAUDE.md §6-3 디자인 토큰 강제 정책 — 색상 하드코딩 금지.
 *         시안 data.js BUDGET_W 색상을 토큰 계열(brand/warm/accent/chip.quiet.bgActive/danger)로 매핑.
 * [사이드 이팩트] design-mock BUDGET.color 는 더 이상 BudgetCategoryBar 에서 사용하지 않음.
 *                 FolioMobile.tsx 가 이 상수를 import — 변경 시 모바일 뷰도 자동 반영.
 * [수정 시 고려사항] 새 카테고리 추가 시 본 맵에 토큰 색상 추가. 디자인 토큰 변경 시 자동 반영.
 */
export const BUDGET_CATEGORY_COLORS: Record<string, string> = {
  transport: c.brand[500][L], // 교통 — 브랜드 블루
  accommodation: c.chip.quiet.bgActive[L], // 숙박 — 조용한 보라 (시안 보라 계열 근사)
  food: c.warm[500][L], // 식비 — 웜 앰버
  activity: c.accent[500][L], // 액티비티 — 그린 액센트
  etc: c.danger[500][L], // 기타 — 레드 (시안 E53E3E 근사)
};

/**
 * [목적] budgetBreakdown API 키(영문) → 한글 카테고리 라벨 매핑 (fe-be-gap-tier2 T14)
 * [이유] folioSchema.budgetBreakdown 키(accommodation/food/transport/activity/etc)와
 *         화면 표시 한글 라벨(숙박/식비/교통/액티비티/기타)을 분리하여 유지보수 용이.
 * [사이드 이팩트] FolioMobile.tsx 가 이 상수를 import — 변경 시 모바일 뷰도 자동 반영.
 * [수정 시 고려사항] budgetBreakdown 키 추가 시 본 맵에도 추가.
 */
export const BUDGET_CATEGORY_LABEL: Record<string, string> = {
  transport: '교통',
  accommodation: '숙박',
  food: '식비',
  activity: '액티비티',
  etc: '기타',
};

/**
 * [목적] budgetBreakdown 실데이터에서 BudgetSlice 배열 파생 — 0원 카테고리 숨김 (ADR-FGQ-OQ6)
 * [이유] T15: formatKrw(1만원 반올림) 기준으로 0원인 카테고리를 미표시.
 *         비율 계산도 필터링 후 잔액 합계 기준으로 산출하여 바 합산 100% 유지.
 * [사이드 이팩트] 없음 (순수 함수). 기존 buildSlicesFromBreakdown 를 T15 규칙으로 교체.
 * [수정 시 고려사항] 0원 필터 기준: Math.round(amt/10000) === 0. formatKrw 변경 시 동기 갱신 필요.
 */
interface BudgetSliceToken {
  cat: string;
  amt: number;
  color: string;
  pct: number;
}

/**
 * [목적] budgetBreakdown 실데이터의 "카테고리 금액" 5개 필드만 한정한 타입
 * [이유] M6 P1(budget-structuring, packages/types): folioSchema.budgetBreakdown 에 perPerson·isEstimate
 *        boolean 메타 필드가 추가되어 Record<string, number> 로 일괄 취급할 수 없게 됐다(boolean 은 number
 *        가 아님). Object.entries 로 blind 순회하면 그 메타 필드까지 "카테고리"로 잘못 렌더될 위험도 있어,
 *        BUDGET_CATEGORY_LABEL 의 5개 키만 명시적으로 골라 순회하도록 근본 수정한다(단순 타입 캐스팅 회피).
 * [사이드 이팩트] 없음(타입 정의) — folio.budgetBreakdown(변수)은 구조적으로 이 타입을 만족(초과 속성은
 *                변수 할당이라 초과 프로퍼티 검사 미적용).
 * [수정 시 고려사항] BUDGET_CATEGORY_LABEL 에 카테고리 키 추가/삭제 시 이 타입도 동기 갱신 필요.
 *                   Record<K,V> 유틸리티 대신 리터럴 interface 를 쓴다 — Record 는 TS 가 매핑 타입을
 *                   인덱스 시그니처처럼 취급해 folio.budgetBreakdown(perPerson·isEstimate 추가 프로퍼티
 *                   보유)을 대입할 때 "초과 프로퍼티 허용" 구조적 타이핑이 깨지는 TS 특이 동작이 있었다.
 */
export interface BudgetBreakdownCategories {
  transport: number;
  accommodation: number;
  food: number;
  activity: number;
  etc: number;
  /** FlowLine A3: 카테고리 금액 자체가 1인 기준이라는 메타 표식 — folioSchema.budgetBreakdown.perPerson 과 동일 */
  perPerson?: boolean;
  /** FlowLine A3: LLM 추정치 표식 — folioSchema.budgetBreakdown.isEstimate 와 동일 */
  isEstimate?: boolean;
}

/**
 * [목적] BudgetBreakdownCategories 중 "카테고리 금액(number)" 5개 필드만 한정한 키 유니온
 * [이유] FlowLine A3: BudgetBreakdownCategories 에 perPerson/isEstimate(boolean) 메타 필드가 추가되면서
 *        `keyof BudgetBreakdownCategories` 로 캐스팅하면 breakdown[key] 의 타입이 number|boolean 으로
 *        넓어져 buildSlicesFromBreakdown 의 산술 연산이 타입 에러가 된다. 카테고리 순회 전용으로
 *        boolean 메타 필드를 제외한 키 유니온을 별도 선언해 이 문제를 근본 해결한다.
 * [사이드 이팩트] 없음(타입 정의) — FolioMobile.tsx BudgetTab 도 동일 문제라 이 타입을 재사용.
 * [수정 시 고려사항] 카테고리 키 추가/삭제 시 BUDGET_CATEGORY_LABEL·BudgetBreakdownCategories 와 함께 갱신.
 */
export type BudgetCategoryKey = 'transport' | 'accommodation' | 'food' | 'activity' | 'etc';

function buildSlicesFromBreakdown(breakdown: BudgetBreakdownCategories): BudgetSliceToken[] {
  // ADR-FGQ-OQ6: 1만원 반올림 후 0인 카테고리 숨김
  // [수정] Object.entries(breakdown) 대신 BUDGET_CATEGORY_LABEL 키만 명시 순회 — perPerson/isEstimate 같은
  //        비-카테고리 메타 필드가 breakdown 객체에 섞여 있어도(M6 P1) 카테고리로 오인되지 않는다.
  // [수정 시 고려사항] BUDGET_CATEGORY_LABEL 은 Record<string,string> 로 선언돼 keyof 가 string 으로
  //                   넓혀지므로, breakdown 인덱싱에는 BudgetBreakdownCategories 자체의 리터럴 키를 사용한다.
  const categoryEntries = (Object.keys(BUDGET_CATEGORY_LABEL) as Array<BudgetCategoryKey>).map(
    (key) => [key, breakdown[key] ?? 0] as const,
  );
  const visible = categoryEntries.filter(([, amt]) => Math.round(amt / 10000) > 0);
  const total = visible.reduce((s, [, v]) => s + v, 0);
  if (total === 0) return [];
  // [수정: QA-P1-04] 카테고리별 비율을 각각 반올림하면 합이 100%를 넘거나 못 미칠 수 있다(예: 101%) —
  // distributePercentages(최대잔여법)로 전체 합이 정확히 100이 되도록 배분.
  const pcts = distributePercentages(visible.map(([, amt]) => amt));
  return visible.map(([key, amt], i) => ({
    cat: BUDGET_CATEGORY_LABEL[key] ?? key,
    amt,
    color: BUDGET_CATEGORY_COLORS[key] ?? BRAND,
    pct: pcts[i]!,
  }));
}

/**
 * [목적] 폴리오 뷰어 예산 섹션 — 총액·인당·카테고리 색상 바 + 도트 범례 (T15 정합)
 * [이유] T15: ADR-FGQ-OQ5/6 규칙을 FolioBudgetPreview 와 동일하게 적용.
 *         perPerson: budgetPerPerson(FE 이미 계산된 값) 있으면 "인당 약 N만원" 표시.
 *         0원 카테고리 숨김(buildSlicesFromBreakdown 내부 필터). "약 N만원" 포맷(formatKrw).
 *         FlowLine A3(71e75fbb): budgetBreakdown 고정 mock(design-mock BUDGET) 폴백 제거 —
 *           null 이면 "예산 정보 없음" 안내로 대체(실제 없는 데이터를 있는 것처럼 보여주지 않음).
 *           isEstimate=true 면 "추정치" 배지, perPerson=true 면 "1인 기준" 배지 표시.
 * [사이드 이팩트] formatKrw import(FolioBudgetPreview) 재사용 — 포맷 정책 단일 진입점 유지.
 *                design-mock BUDGET/buildSlicesFromMock 삭제 — 이 컴포넌트가 유일한 소비처였음.
 * [수정 시 고려사항] budgetPerPerson 은 folio.budgetPerPerson(BE 직렬화 값) — headcount/totalEstimated FE 계산 아님.
 *                   folio 응답에 totalEstimated 가 없으면 인당 계산 불가 → budgetPerPerson 값 그대로 사용.
 *                   budgetBreakdown 키 추가 시 BUDGET_CATEGORY_COLORS·BUDGET_CATEGORY_LABEL 동기 갱신.
 */
function BudgetCategoryBar({
  budgetPerPerson,
  budgetBreakdown,
}: {
  budgetPerPerson: number | null;
  budgetBreakdown?: BudgetBreakdownCategories | null;
}): JSX.Element | null {
  // [목적] 범례 ↔ 스택바 호버 연동
  // [이유] 2026-09-20 디자인 QA: 바는 비율로 그려지는데 범례는 금액만 보여줘 "어느 색이 어느 항목인지"
  //        를 색 점 하나로만 대응시켜야 했다. 호버 시 해당 세그먼트만 남기고 나머지를 흐리게 해
  //        두 요소를 명시적으로 연결한다(마우스 없는 환경을 위해 퍼센트 배지도 함께 노출).
  // [수정 시 고려사항] hooks 규칙상 아래 early return 보다 반드시 위에 선언해야 한다.
  const [focusCat, setFocusCat] = useState<string | null>(null);

  // budgetPerPerson=null 이면 예산 섹션 전체 미표시 (기존 정책·기존 테스트 보존)
  if (!budgetPerPerson) return null;

  // FlowLine A3: mock 폴백 제거 — budgetBreakdown 없으면 slices=[] 로 "예산 정보 없음" 분기
  const slices: BudgetSliceToken[] = budgetBreakdown
    ? buildSlicesFromBreakdown(budgetBreakdown)
    : [];
  const isEstimate = budgetBreakdown?.isEstimate === true;
  const isPerPersonAmounts = budgetBreakdown?.perPerson === true;

  // 인당 "약 N만원" 표시 (budgetPerPerson 존재 시)
  const perPersonLabel = budgetPerPerson ? formatKrw(budgetPerPerson) : null;

  return (
    <Box
      sx={{
        bgcolor: PAPER2,
        // [수정: 디자인 개편 2026-09-20] 배경색만 있고 테두리가 없어 카드 경계가 모호했다.
        border: `1px solid ${BORDER}`,
        borderRadius: '14px',
        p: `${SP[4]}px`,
        mt: `${SP[5]}px`,
      }}
      aria-label="예산 카테고리 분배"
    >
      {/* 헤더: 총 예산 분배 라벨 + 배지(추정치/1인 기준) + 인당 금액 (T15 ADR-FGQ-OQ5, A3 배지) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          mb: `${SP[2]}px`,
          gap: `${SP[2]}px`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={`${SP[1]}px`} flexWrap="wrap">
          <Typography
            sx={{
              fontSize: 12,
              color: INK_HI,
              fontWeight: 800,
              letterSpacing: '-0.2px',
              lineHeight: 1.3,
            }}
          >
            총 예산 분배
          </Typography>
          {isEstimate && (
            <Box
              component="span"
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                color: INK_MID,
                border: `1px solid ${BORDER}`,
                borderRadius: `${R.s}px`,
                px: `${SP[1]}px`,
                py: '1px',
              }}
            >
              추정치
            </Box>
          )}
          {isPerPersonAmounts && (
            <Box
              component="span"
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                color: BRAND,
                border: `1px solid ${BRAND}`,
                borderRadius: `${R.s}px`,
                px: `${SP[1]}px`,
                py: '1px',
              }}
            >
              1인 기준
            </Box>
          )}
        </Stack>
        {perPersonLabel && (
          <Typography
            sx={{
              // [수정: 디자인 개편 2026-09-20] 이 카드에서 가장 중요한 수치인데 10px 라 배지보다도
              // 작아 보였다 — 히어로 넘버로 승격.
              fontSize: 19,
              color: BRAND,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              fontWeight: 700,
              letterSpacing: '-0.6px',
              // formatKrw 결과("약 20만원")의 공백이 모노 폭을 먹어 "약    20만원" 처럼 벌어져 보인다.
              wordSpacing: '-3px',
              lineHeight: 1.2,
              flexShrink: 0,
            }}
            aria-label={`인당 ${perPersonLabel}`}
          >
            인당 {perPersonLabel}
          </Typography>
        )}
      </Box>

      {/* FlowLine A3: budgetBreakdown 없으면 고정 mock 대신 "예산 정보 없음" 안내 */}
      {!budgetBreakdown && (
        <Typography sx={{ fontSize: 11, color: INK_LOW, textAlign: 'center', py: `${SP[2]}px` }}>
          예산 정보 없음
        </Typography>
      )}

      {slices.length > 0 && (
        <>
          {/* 카테고리 색상 바 — folio.jsx 시안 L124-126 */}
          <Box
            role="img"
            aria-label={`예산 분배 바: ${slices.map((b) => `${b.cat} ${b.pct}%`).join(', ')}`}
            sx={{
              display: 'flex',
              // [수정: 디자인 개편 2026-09-20] 8px 바는 납작하고, 비중이 작은 카테고리(3% ≈ 12px)는
              // 사실상 보이지 않았다. 높이를 키우고 트랙·세그먼트 최소폭을 줌다.
              height: 14,
              bgcolor: c.paper[0][L],
              boxShadow: `inset 0 0 0 1px ${BORDER}`,
              borderRadius: `${R.xl}px`,
              overflow: 'hidden',
              mb: `${SP[3]}px`,
            }}
          >
            {slices.map((b, idx) => (
              <Box
                key={b.cat}
                title={`${b.cat} · ${formatKrw(b.amt)} (${b.pct}%)`}
                onMouseEnter={() => setFocusCat(b.cat)}
                onMouseLeave={() => setFocusCat(null)}
                sx={{
                  flex: b.pct,
                  minWidth: 6,
                  bgcolor: b.color,
                  // 세그먼트 경계 — 인접한 색이 붙어 버려 구분이 안 되던 문제 해결
                  borderLeft: idx === 0 ? 'none' : `2px solid ${PAPER2}`,
                  boxSizing: 'border-box',
                  opacity: focusCat && focusCat !== b.cat ? 0.28 : 1,
                  transition: `opacity ${MO.fast.duration}ms`,
                }}
              />
            ))}
          </Box>
          {/* 도트 범례 그리드 — folio.jsx 시안 L127-135, "약 N만원" 포맷 (ADR-FGQ-OQ6) */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              columnGap: `${SP[5]}px`,
              rowGap: 0,
            }}
          >
            {slices.map((b, idx) => (
              <Box
                key={b.cat}
                onMouseEnter={() => setFocusCat(b.cat)}
                onMouseLeave={() => setFocusCat(null)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: `${SP[2]}px`,
                  fontSize: 11,
                  minWidth: 0,
                  py: '7px',
                  pr: `${SP[2]}px`,
                  borderRadius: `${R.s}px`,
                  // [수정: 디자인 개편 2026-09-20] 2열 그리드의 "마지막 행"은 구분선을 뺀다.
                  // 항목이 5개(홀수)라 기존엔 끝에 선만 덩그러니 남아 끊긴 것처럼 보였다.
                  // nth-last-child(-n+2) 는 항목 수가 홀수/짝수여도 각 열의 마지막 행을 정확히 잡는다.
                  borderBottom: idx >= slices.length - 2 ? 'none' : `1px solid ${BORDER_SOFT}`,
                  bgcolor: focusCat === b.cat ? c.paper[0][L] : 'transparent',
                  opacity: focusCat && focusCat !== b.cat ? 0.45 : 1,
                  transition: `background-color ${MO.fast.duration}ms, opacity ${MO.fast.duration}ms`,
                }}
              >
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: b.color, flexShrink: 0 }}
                />
                {/* 라벨 자체는 카테고리명만 담는다 — 퍼센트는 별도 노드로 분리 */}
                <Box
                  component="span"
                  sx={{
                    color: INK_MID,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {b.cat}
                </Box>
                {/* [수정: 디자인 개편 2026-09-20] 비중(%) 배지 — 이전엔 aria-label 안에만 있고
                    화면에는 없어서 바 길이를 눈으로 재야 했다. */}
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                    fontSize: 9.5,
                    fontWeight: 600,
                    color: INK_LOW,
                    bgcolor: c.paper[0][L],
                    border: `1px solid ${BORDER}`,
                    borderRadius: `${R.xl}px`,
                    px: '5px',
                    lineHeight: 1.5,
                  }}
                >
                  {`${b.pct}%`}
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: INK_HI,
                    fontWeight: 700,
                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                    fontSize: 9.5,
                    wordSpacing: '-2px',
                    marginLeft: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatKrw(b.amt)}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

// ─── ForkButton ────────────────────────────────────────────────────────────

/**
 * [목적] Fork CTA 버튼 — 로그인 사용자는 fork 실행, guest 는 /login?returnTo 로 redirect
 * [이유] WC-FE-FOLIO-05 — fork API 연결 + guest redirect 처리
 * [사이드 이팩트] fork 성공 시 navigate('/folio/:newId'). folioList 캐시 invalidate (useForkFolio 내부)
 * [수정 시 고려사항] fork 진행 중(isPending) 버튼 비활성화. 실패 시 onForkError 콜백으로 상위에 전달
 */
function ForkButton({
  folioId,
  onForkError,
  fullWidth = false,
}: {
  folioId: string;
  onForkError?: () => void;
  fullWidth?: boolean;
}): JSX.Element {
  const { status } = useAuth();
  const navigate = useNavigate();
  const fork = useForkFolio();

  const handleClick = () => {
    // guest → login redirect (WC-FE-FOLIO-05 AC-3)
    if (status === 'guest') {
      navigate(`/login?returnTo=${encodeURIComponent(`/folio/${folioId}`)}`);
      return;
    }
    fork.mutate(
      { folioId },
      {
        onSuccess: (newFolio) => {
          navigate(`/folio/${newFolio.id}`);
        },
        onError: () => {
          onForkError?.();
        },
      },
    );
  };

  return (
    <ButtonBase
      onClick={handleClick}
      aria-label={
        status === 'guest' ? '내 폴리오로 가져오기 — 로그인 후 이용 가능' : '내 폴리오로 가져오기'
      }
      disabled={fork.isPending}
      sx={{
        // [수정: 디자인 개편] 처리 중에도 opacity만 낮춰 파란+흰색 글자가 그대로 남았다 — 로딩 중에는 회색 면으로 명시한다.
        bgcolor: fork.isPending ? PAPER2 : BRAND,
        color: fork.isPending ? INK_LOW : c.paper[0][L],
        // [수정: 디자인 개편 2026-09-20] 주 CTA 는 라운드를 한 단계 키워(R.s=6 → R.m=10) 보조 버튼과 구분한다.
        borderRadius: fullWidth ? `${R.m}px` : `${R.s}px`,
        // [수정: 디자인 개편] 주 액션(fullWidth)이면 전체폭+큰 글자로 시각적 우선순위를 높인다.
        width: fullWidth ? '100%' : undefined,
        justifyContent: fullWidth ? 'center' : undefined,
        px: fullWidth ? `${SP[4]}px` : `${SP[3]}px`,
        // [수정] py 대신 고정 높이 — 아이콘(LineIcon)과 텍스트의 line-height 차이로 높이가 흔들리던 문제 제거.
        height: fullWidth ? 46 : 30,
        fontSize: fullWidth ? 14 : 11.5,
        fontWeight: 700,
        letterSpacing: '-0.2px',
        gap: `${SP[1]}px`,
        // [수정: 디자인 개편] 주 CTA 에만 브랜드 그림자 + hover 리프트 — 화면에서 가장 먼저 눈에 들어와야 한다.
        boxShadow:
          fullWidth && !fork.isPending
            ? `0 1px 2px ${BRAND}40, 0 8px 18px -8px ${BRAND}8C`
            : 'none',
        transition: `opacity ${MO.fast.duration}ms, transform ${MO.fast.duration}ms, box-shadow ${MO.fast.duration}ms`,
        '&:hover': fullWidth && !fork.isPending ? { transform: 'translateY(-1px)' } : undefined,
        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
        '&:disabled': { cursor: 'not-allowed' },
      }}
    >
      {fork.isPending ? (
        <CircularProgress size={12} sx={{ color: 'inherit', mr: `${SP[1]}px` }} />
      ) : (
        // [수정: 디자인 개편] '⑂' 글리프가 Pretendard 에 없어 시스템 폴백(두부/엉뚱한 모양)으로 렌더됐다 → 라인 아이콘.
        <LineIcon name="fork" size={fullWidth ? 16 : 13} aria-hidden />
      )}
      {fullWidth ? '내 폴리오로 가져오기' : 'Fork'}
    </ButtonBase>
  );
}

// ─── LikeButton / BookmarkButton ────────────────────────────────────────────

/**
 * [목적] 좋아요 토글 버튼 — useFolioLike 훅 + useAuthGate('folio.like') 게스트 가드
 * [이유] fe-folio-social-wire — folio-social.client.ts like/unlike 를 FolioPage 에 최초 배선.
 * [사이드 이팩트] 낙관적 업데이트로 즉시 하트/카운트 반영, 실패 시 자동 롤백(useFolioLike 내부).
 * [수정 시 고려사항] 색만으로 좋아요 여부를 전달하지 않도록 하트 기호(♥/♡) 자체를 상태 표시로 사용.
 */
function LikeButton({ folio }: { folio: Folio }): JSX.Element {
  const { liked, likeCount, toggle, isPending } = useFolioLike(
    folio.id,
    folio.likeCount,
    folio.viewer?.liked,
  );
  const { guard, isPromptOpen, closePrompt, pendingIntent } = useAuthGate('folio.like');

  return (
    <>
      <ButtonBase
        onClick={() => guard(toggle)}
        disabled={isPending}
        aria-label={liked ? '좋아요 취소' : '좋아요'}
        aria-pressed={liked}
        sx={{
          ...actionGhostSx,
          bgcolor: liked ? `${c.danger[500][L]}14` : 'transparent',
          color: liked ? c.danger[500][L] : INK_LOW,
          border: `1px solid ${liked ? c.danger[500][L] : 'transparent'}`,
          opacity: isPending ? 0.7 : 1,
          '&:hover': liked ? undefined : { bgcolor: PAPER2, color: INK_HI },
        }}
      >
        <Box component="span" aria-hidden="true">
          {liked ? '♥' : '♡'}
        </Box>
        {likeCount}
      </ButtonBase>
      <LoginPromptModal open={isPromptOpen} intent={pendingIntent} onClose={closePrompt} />
    </>
  );
}

/**
 * [목적] 북마크 토글 버튼 — useFolioBookmark 훅 + useAuthGate('folio.bookmark') 게스트 가드
 * [이유] fe-folio-social-wire — folio-social.client.ts bookmark/unbookmark 를 FolioPage 에 최초 배선.
 * [사이드 이팩트] 낙관적 업데이트로 즉시 아이콘/카운트 반영, 실패 시 자동 롤백(useFolioBookmark 내부).
 * [수정 시 고려사항] 저장됨 상태는 색+아이콘(🔖/☆)+텍스트로 함께 표시(색만으로 전달 금지).
 */
function BookmarkButton({ folio }: { folio: Folio }): JSX.Element {
  const { bookmarked, bookmarkCount, toggle, isPending } = useFolioBookmark(
    folio.id,
    folio.bookmarkCount,
    folio.viewer?.bookmarked,
  );
  const { guard, isPromptOpen, closePrompt, pendingIntent } = useAuthGate('folio.bookmark');

  return (
    <>
      <ButtonBase
        onClick={() => guard(toggle)}
        disabled={isPending}
        aria-label={bookmarked ? '북마크 취소' : '북마크'}
        aria-pressed={bookmarked}
        sx={{
          ...actionGhostSx,
          bgcolor: bookmarked ? `${BRAND}14` : 'transparent',
          color: bookmarked ? BRAND : INK_LOW,
          border: `1px solid ${bookmarked ? BRAND : 'transparent'}`,
          opacity: isPending ? 0.7 : 1,
          '&:hover': bookmarked ? undefined : { bgcolor: PAPER2, color: INK_HI },
        }}
      >
        <Box component="span" aria-hidden="true">
          {bookmarked ? '🔖' : '☆'}
        </Box>
        {bookmarkCount}
      </ButtonBase>
      <LoginPromptModal open={isPromptOpen} intent={pendingIntent} onClose={closePrompt} />
    </>
  );
}

// ─── FolioContent ──────────────────────────────────────────────────────────

/**
 * [목적] 폴리오 뷰 본문 — Day 탭 + 타임라인 카드 + 지도
 * [이유] WC-FE-FOLIO-01/05 — mock 제거 후 실 데이터 기반 렌더링 + Fork CTA 연결.
 *         fe-folio-social-wire — 좋아요/북마크/댓글 실배선 완료(LikeButton/BookmarkButton/CommentSection).
 * [사이드 이팩트] fork 실패 시 Snackbar 오류 메시지 표시. 좋아요/북마크/댓글 게스트 시도는 LoginPromptModal 노출.
 * [수정 시 고려사항] 좋아요/북마크 상태는 새로고침 시 리셋됨(BE isLiked/isBookmarked 미제공, 상단 헤더 주석 참조)
 */
function FolioContent({ folio }: { folio: Folio }): JSX.Element {
  const { viewport, openSidebar } = useShell();
  const navigate = useNavigate();
  const splitMode = !viewport.isMobile;
  const days = folio.days ?? [];
  const [activeDay, setActiveDay] = useState<number>(0);
  const [hover, setHover] = useState<number | null>(null);
  const [forkError, setForkError] = useState(false);
  // [목적] 타임라인 아이템 DOM 참조 — 지도 마커 클릭 시 해당 카드로 스크롤 포커스(A4)
  // [이유] FolioMap onFocusItem(index) 콜백에서 절대 인덱스로 해당 카드에 scrollIntoView 호출.
  // [사이드 이팩트] 없음(렌더 후 ref 콜백으로 채워지는 순수 DOM 참조 맵).
  // [수정 시 고려사항] Day 전환 시 이전 Day 의 ref 는 자연히 언마운트되어 갱신됨.
  const itemRefs = useRef<Record<number, HTMLElement | null>>({});
  // [목적] 소유자 여부 판정 — FlowLine A9: 편집 버튼은 소유자에게만.
  // [이유] folio.ownerUserId 가 있으면(로그인 사용자 소유) 로그인+id 일치로 판정.
  //        ownerUserId=null(anon 세션 생성)은 API 가 anon_session_id 를 노출하지 않아 클라이언트가 진짜
  //        소유자인지 검증할 수 없다 — 최소 침습 휴리스틱으로 "로그인하지 않은 방문자"에게만 편집을 보여
  //        기존 anon 작성 흐름을 보존한다(실제 인가는 BE PATCH/DELETE 가 최종 검증, 근본 보안은 서버 책임).
  // [수정 시 고려사항] BE 가 향후 viewer.isOwner 같은 필드를 내려주면 이 휴리스틱을 대체할 것.
  const { user, status } = useAuth();
  const isOwner = folio.ownerUserId
    ? status === 'authenticated' && folio.ownerUserId === user?.id
    : status !== 'authenticated';
  // FlowLine A7: 폴리오 신고 다이얼로그 상태 + 게스트 가드
  const [folioReportOpen, setFolioReportOpen] = useState(false);
  const folioReportGate = useAuthGate('report.create');
  // FlowLine A8: Day 가져오기 다이얼로그 상태(현재 활성 Day 기준)
  const [dayImportOpen, setDayImportOpen] = useState(false);
  // [목적] "조용한" 필터 토글 상태 — plan-generate 재생성 payload 에 quiet: boolean 으로 전달 예정
  // [이유] WC-TOURAPI-QR-5-02 — 폴리오 뷰에서도 동일 칩 라인업 제공 (plan-generate Spec 확정 전 boolean flag)
  // [사이드 이팩트] 없음 (로컬 UI 상태)
  // [수정 시 고려사항] plan-generate Spec 확정 후 재생성 API 호출 payload 에 { quiet } 포함 필요
  const [quiet, setQuiet] = useState<boolean>(false);
  // [목적] 선택된 PlanItem 의 TourAPI content_id — CongestionForecastCard 에 전달
  // [이유] WC-TOURAPI-QR-5-03 + [G2-2, 2026-09-17]: PlanItem.poiId 는 항상 null 이라
  //        factInfo.contentId(계약 C2)를 GET /pois/:id/congestion 호출 키로 사용한다.
  // [사이드 이팩트] usePoiCongestion 훅이 selectedPoiId(=contentId)로 GET /pois/:id/congestion 호출
  // [수정 시 고려사항] factInfo/contentId 없으면 null 유지 — 카드 미표시(graceful)
  const [selectedPoiId, setSelectedPoiId] = useState<string | null>(null);
  const congestion = usePoiCongestion(selectedPoiId ?? undefined);
  const day = days[activeDay];

  // [목적] 지도에서 확대해 보여줄 일정 항목 인덱스 — 타임라인 카드 클릭으로 확정된 선택
  // [이유] [2026-09-21 사용자 요청] "장소를 클릭하면 그 지역이 확대되게". 기존에는 hover 상태만 있어
  //        마우스를 떼면 즉시 풀렸고, SDK 지도는 hover 를 아예 쓰지 않아 반응이 없었다.
  //        hover(일시적 하이라이트)와 selection(확정 포커스)을 분리해 클릭한 장소가 지도에 고정되게 한다.
  // [사이드 이팩트] FolioMap 의 focusedIndex 로 전달 → 해당 좌표로 panTo + 확대, 마커 활성 표시.
  //                 null 로 돌아가면 전체 동선 뷰(setBounds)로 복귀한다.
  // [수정 시 고려사항] Day 탭을 바꾸면 인덱스 의미가 달라지므로 반드시 초기화한다(아래 useEffect).
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Day 전환 시 이전 Day 의 인덱스가 남아 엉뚱한 장소가 확대되는 것을 막는다.
  useEffect(() => {
    setFocusedIndex(null);
    setSelectedPoiId(null);
  }, [activeDay]);

  // [목적] 지도 마커/핀 클릭 → 타임라인 카드 하이라이트 + 스크롤 포커스(A4)
  // [이유] FolioMap onFocusItem 콜백 — hover 상태를 재사용해 기존 카드 하이라이트 스타일과 일관.
  // [사이드 이팩트] 없음(스크롤은 부수효과지만 상태 변경 없음)
  // [수정 시 고려사항] itemRefs 에 없는 인덱스(아직 렌더 전)는 scrollIntoView 를 건너뜀(no-op).
  function handleFocusItem(index: number): void {
    setHover(index);
    // [2026-09-21] 지도 마커 클릭도 "확정 선택"으로 취급 — 클릭한 장소가 지도에서 계속 강조된다.
    setFocusedIndex(index);
    itemRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  return (
    <>
      <TopBar
        title={folio.title}
        breadcrumb={`FOLIO · #${folio.slug}`}
        mobile={viewport.isMobile}
        onBurger={openSidebar}
      />
      <Box sx={{ display: 'flex', height: 'calc(100vh - 54px)', bgcolor: PAPER1 }}>
        {/* LEFT: 일정 리스트 */}
        <Box
          sx={{
            flex: splitMode ? '0 0 460px' : '1',
            borderRight: splitMode ? `1px solid ${BORDER}` : 'none',
            overflowY: 'auto',
            bgcolor: c.paper[0][L],
          }}
        >
          {/* 폴리오 헤더 */}
          <Box
            sx={{
              p: `${SP[6]}px ${SP[6]}px ${SP[4]}px`,
              borderBottom: `1px solid ${BORDER_SOFT}`,
            }}
          >
            {/* T15: 작성자 author.handle 우선, 부재 시 @slug 폴백. viewCount 표시(G8). */}
            <Typography
              sx={{
                fontSize: 10,
                color: INK_LOW,
                fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                // [수정: 디자인 개편 2026-09-20] 작은 메타 캐션은 트래킹을 넣어야 라벨처럼 읽힌다.
                letterSpacing: '1.2px',
                lineHeight: 1.4,
                mb: `${SP[3]}px`,
              }}
            >
              {/* [수정] 2026-09-18 QA(M-4): 작성자 없는 폴리오에 slug 를 핸들처럼 찍던 폴백 제거.
                  slug 는 아래 `#{folio.slug}` 로 이미 별도 표기된다. */}
              {/* [수정: 디자인 개편 2026-09-20] ❤ 좋아요·⑂ Fork 카운트 제거 — 바로 아래 액션 바의
                  LikeButton/BookmarkButton 과 같은 수치를 중복 노출하고 있었다(같은 화면 두 곳에 동일 정보).
                  게다가 '⑂' 는 Pretendard 에 글리프가 없어 시스템 폴백으로 깨져 보였다.
                  조회수(👁)는 이 줄에만 있는 고유 정보라 유지한다. */}
              {folioAuthorLabel(folio.author)}
              {' · '}
              FOLIO
              {/* [수정: 2026-09-20] 조회수 글리프 '👁'(이모지) → LineIcon 'eye'.
                  이모지는 플랫폼마다 컬러 그림으로 렌더돼 모노 캡션 줄에서 혼자 튀고 사이드바/액션바의
                  라인 아이콘 세트와 굵기·톤이 어긋났다. 아이콘은 currentColor 를 상속해 INK_LOW 를 그대로 따른다.
                  스크린리더용 '조회수' 텍스트는 시각적으로 감춘 채 유지(이모지 시절엔 의미 전달이 없었다). */}
              {folio.viewCount != null && (
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', ml: '6px' }}
                >
                  <Box component="span" aria-hidden>
                    ·
                  </Box>
                  <LineIcon name="eye" size={12} sx={{ ml: '2px' }} aria-hidden />
                  <Box component="span" sx={visuallyHiddenSx}>
                    조회수
                  </Box>
                  {folio.viewCount.toLocaleString()}
                </Box>
              )}
            </Typography>
            <Typography
              component="h1"
              sx={{
                // [수정: 디자인 개편 2026-09-20] 19px 제목은 바로 아래 12px 본문·11.5px 버튼과 대비가 약해
                // 페이지 제목이 아닌 소제목처럼 보였다. 폴리오 상세의 주인공이므로 한 단계 키운다.
                fontSize: 26,
                fontWeight: 800,
                color: INK_HI,
                letterSpacing: '-0.9px',
                m: 0,
                mb: `${SP[2]}px`,
                lineHeight: 1.26,
              }}
            >
              {folio.title}
            </Typography>
            {folio.subtitle && (
              <Typography
                sx={{ fontSize: 13.5, color: INK_MID, lineHeight: 1.65, mb: `${SP[4]}px` }}
              >
                {folio.subtitle}
              </Typography>
            )}
            {/* [수정: 디자인 개편] 액션을 "주 액션 1개 + 보조 카운터 묶음"으로 재구성 — 이전에는 Fork/좋아요/북마크/공유/PDF/편집이
                모두 같은 크기의 버튼으로 나열되어 어느 것이 핵심 행동인지 구분이 안 됐다. 가져오기를 전체폭 주 버튼으로
                따로 띄우고, 나머지는 작은 보조 줄에 모은다. */}
            <Box sx={{ mb: `${SP[3]}px` }}>
              <ForkButton folioId={folio.id} onForkError={() => setForkError(true)} fullWidth />
            </Box>
            {/* [수정: 디자인 개편 2026-09-20] 주 CTA 와 보조 액션을 상단 구분선으로 분리 —
                이전에는 둘이 다닥붙어 있어 동등한 버튼 묶음처럼 보였다. */}
            <Stack
              direction="row"
              spacing={0}
              flexWrap="wrap"
              gap="5px"
              alignItems="center"
              sx={{ pt: `${SP[3]}px`, borderTop: `1px solid ${BORDER_SOFT}` }}
            >
              {/* 좋아요 / 북마크 / 댓글수 — 보조 카운터 묶음 */}
              <LikeButton folio={folio} />
              <BookmarkButton folio={folio} />
              <Box
                sx={{ ...actionGhostSx, color: INK_LOW, cursor: 'default' }}
                aria-label={`댓글 ${folio.commentCount}개`}
              >
                <Box component="span" aria-hidden="true">
                  💬
                </Box>
                {folio.commentCount}
              </Box>

              {/* "조용한" 칩 — feature flag + chip.quiet 토큰
                  [목적] 폴리오 뷰에서 "조용한" 필터 칩 제공 — plan-generate 재생성 payload boolean flag
                  [이유] WC-TOURAPI-QR-5-02/05 — 채팅 화면과 동일한 칩 라인업 유지
                  [사이드 이팩트] quiet 상태 토글 — plan-generate Spec 확정 후 재생성 API 에 { quiet } 포함
                  [수정 시 고려사항] disabled ButtonBase 에 Tooltip 적용 시 span wrapper 필요 (MUI v5) */}
              {(() => {
                const isDisabled = !QUIET_CHIP_ENABLED;
                const chipEl = (
                  <ButtonBase
                    onClick={() => !isDisabled && setQuiet((v) => !v)}
                    aria-label="조용한 여행 필터 활성화"
                    aria-pressed={quiet}
                    disabled={isDisabled}
                    sx={{
                      ...actionOutlineSx,
                      bgcolor: isDisabled ? 'transparent' : quiet ? QUIET_BG_ACTIVE : QUIET_BG,
                      // [수정: 디자인 개편 2026-09-20] 비활성은 점선 테두리로 "준비 중"을 모양으로도 전달
                      // (opacity 만 낮추면 손상된 버튼처럼 보인다).
                      border: `1px ${isDisabled ? 'dashed' : 'solid'} ${
                        isDisabled ? BORDER : quiet ? QUIET_BG_ACTIVE : QUIET_BORDER
                      }`,
                      color: isDisabled ? INK_LOW : quiet ? QUIET_FG_ACTIVE : QUIET_FG,
                      fontWeight: quiet ? 700 : 500,
                      opacity: isDisabled ? 0.6 : 1,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      '&:hover': isDisabled ? undefined : { bgcolor: QUIET_BG },
                      '&:focus-visible': {
                        outline: `2px solid ${QUIET_BORDER}`,
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    🤫 조용한
                  </ButtonBase>
                );
                return isDisabled ? (
                  <Tooltip title="준비 중 — 활용 신청 후 활성화" arrow placement="top">
                    <span>{chipEl}</span>
                  </Tooltip>
                ) : (
                  chipEl
                );
              })()}

              {/* FlowLine A1: 공유 — navigator.share/클립보드 복사 + private 전환 안내(FolioShareButton 내부) */}
              <FolioShareButton folio={folio} isOwner={isOwner} sx={actionOutlineSx}>
                공유
              </FolioShareButton>
              {/* FlowLine A2: PDF 내보내기 — 요청/폴링/자동 다운로드/재시도(FolioExportButton 내부) */}
              <FolioExportButton folioId={folio.id} sx={actionOutlineSx} />
              {/* FlowLine A9: 편집 버튼은 소유자에게만 노출 */}
              {isOwner && (
                <ButtonBase
                  onClick={() => navigate(`/folio/${folio.id}/edit`)}
                  aria-label="편집"
                  sx={actionOutlineSx}
                >
                  ✎ 편집
                </ButtonBase>
              )}
              {/* FlowLine A8: Day 가져오기 — 비소유자·로그인 사용자에게만, 현재 활성 Day 기준 */}
              {!isOwner && status === 'authenticated' && day && (
                <ButtonBase
                  onClick={() => setDayImportOpen(true)}
                  aria-label={`Day ${day.dayIndex} 내 폴리오로 가져오기`}
                  sx={actionOutlineSx}
                >
                  {/* [수정: 디자인 개편 2026-09-20] 기존 다운로드 글리프는 Pretendard 에 없어
                      두부(□)로 렌더됐다 → 공용 라인 아이콘으로 교체.
                      또 "Day 가져오기"는 이 행에서 가장 긴 라벨이라 액션 바를 두 줄로 밀어냈다 —
                      "Day" 로 줄이고 전체 의미는 aria-label 이 담당한다. */}
                  <LineIcon name="download" size={13} aria-hidden />
                  Day
                </ButtonBase>
              )}
              {/* FlowLine A7: 폴리오 신고 — 비소유자에게만, 게스트는 useAuthGate('report.create') 로 가드 */}
              {!isOwner && (
                <ButtonBase
                  onClick={() => folioReportGate.guard(() => setFolioReportOpen(true))}
                  aria-label="폴리오 신고"
                  sx={{
                    ...actionGhostSx,
                    // [수정: 디자인 개편 2026-09-20] 신고는 드물게 쓰이는 파괴적 행동이다 — 공유/PDF 와 같은
                    // 아웃라인 무게를 주면 안 된다. 텍스트 링크 수준으로 낮추고 우측 끝으로 분리한다.
                    marginLeft: 'auto',
                    px: `${SP[1]}px`,
                    fontWeight: 500,
                    fontSize: 11,
                    color: INK_LOW,
                    opacity: 0.75,
                    '&:hover': { opacity: 1, textDecoration: 'underline' },
                  }}
                >
                  신고
                </ButtonBase>
              )}
            </Stack>
          </Box>

          {/* 데이 탭
              [수정: 디자인 개편 2026-09-20] "파란 블록 1개 + 회색 블록 N개"는 탭이 아니라 버튼 나열로 읽혔다.
              iOS/웹 표준인 세그먼티드 컨트롤(트랙 위에 알약 하나가 올라오는 형태)로 바꿔 "여러 개 중 하나 선택"
              을 모양으로 전달한다. 트랙은 의미 없는 장식이므로 DOM 노드 추가 없이 ::before 로 그린다. */}
          <Box
            sx={{
              display: 'flex',
              position: 'sticky',
              top: 0,
              p: `${SP[3]}px ${SP[5]}px ${SP[3]}px`,
              gap: 0,
              bgcolor: c.paper[0][L],
              borderBottom: `1px solid ${BORDER_SOFT}`,
              zIndex: 2,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: `${SP[5]}px`,
                right: `${SP[5]}px`,
                top: `${SP[3]}px`,
                bottom: `${SP[3]}px`,
                bgcolor: PAPER2,
                borderRadius: `${R.m}px`,
              },
            }}
          >
            {days.map((d, i) => {
              const active = activeDay === i;
              return (
                <ButtonBase
                  key={d.id}
                  onClick={() => setActiveDay(i)}
                  aria-label={`Day ${d.dayIndex}`}
                  aria-pressed={active}
                  sx={{
                    flex: 1,
                    // 트랙(::before) 위에 올라가도록 z-index 부여 + 트랙 안쪽 여백 3px
                    position: 'relative',
                    zIndex: 1,
                    m: '3px',
                    minHeight: ACTION_H,
                    bgcolor: active ? BRAND : 'transparent',
                    color: active ? c.paper[0][L] : INK_LOW,
                    boxShadow: active ? `0 1px 3px ${BRAND}59` : 'none',
                    borderRadius: '9px',
                    p: `${SP[1]}px ${SP[2]}px`,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '-0.1px',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    transition: `background-color ${MO.fast.duration}ms, color ${MO.fast.duration}ms, box-shadow ${MO.fast.duration}ms`,
                    '&:hover': active
                      ? undefined
                      : { bgcolor: `${c.paper[0][L]}BF`, color: INK_HI },
                    '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: '2px' },
                  }}
                >
                  <Box>Day {d.dayIndex}</Box>
                  {d.date && (
                    <Box
                      sx={{
                        fontSize: 9.5,
                        opacity: 0.85,
                        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                        mt: `${SP[0]}px`,
                        fontWeight: 500,
                      }}
                    >
                      {d.date}
                    </Box>
                  )}
                </ButtonBase>
              );
            })}
          </Box>

          {/* 일정 카드 */}
          {day && (
            <Box sx={{ p: `${SP[4]}px ${SP[5]}px ${SP[8]}px` }}>
              {/* [목적] Day 서사 요약 한 줄
                  [이유] 2026-09-18 QA(M-1): JetBrains Mono 는 한글 글리프가 없어 시스템 폴백으로 넘어가면서
                         글자마다 모노 폭을 먹어 어절 간격이 들쭉날쭉했다. LLM 이 쓴 한글 산문이므로
                         본문 폰트(기본 typography)를 쓴다. 모노는 slug·단축키·수치 라벨 전용.
                  [사이드 이팩트] 같은 자리에 영문 "Day N" 폴백이 올 때도 본문 폰트로 렌더된다.
                  [수정 시 고려사항] 이 영역에 모노를 다시 적용하지 말 것 — 한글 본문이 들어오는 자리다. */}
              <Typography
                sx={{
                  // [수정: 디자인 개편 2026-09-20] 11px 회색 한 줄로 떠 있어 본문인지 캐션인지 구분이 안 됐다.
                  // Day 서사 요약은 타임라인의 도입부이므로 콜아웃 박스로 승격해 읽히게 만든다.
                  fontSize: 12.5,
                  color: INK_MID,
                  lineHeight: 1.75,
                  bgcolor: PAPER2,
                  borderLeft: `3px solid ${BRAND}66`,
                  borderRadius: `4px ${R.m}px ${R.m}px 4px`,
                  p: `${SP[3]}px ${SP[3]}px`,
                  mb: `${SP[4]}px`,
                }}
              >
                {day.summary ?? `Day ${day.dayIndex}`}
              </Typography>
              {/* [목적] Day 날씨 표시 — T13 ADR-TR-6: day.weather 있으면 표시, 날짜가 있는데 예보가 없으면
                        "예보 없음" 안내(FlowLine A6, 88382b5b) */}
              {/* [이유] dayPlanSchema.weather({code,tempMin,tempMax,pop}) nullable — null이면 날짜 유무로 분기. */}
              {/* [사이드 이팩트] 레이아웃 회귀 없음 — 추가 행만 표시. */}
              {/* [수정 시 고려사항] date 도 없는 폴리오(생성만 하고 날짜 미확정)는 "예보 없음"도 생략(불필요한 안내 방지). */}
              {day.weather ? (
                <Typography
                  sx={{
                    fontSize: 11,
                    color: INK_MID,
                    mb: `${SP[1]}px`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: `${SP[1]}px`,
                  }}
                >
                  <WeatherLabel weather={day.weather} />
                </Typography>
              ) : (
                day.date && (
                  <Typography sx={{ fontSize: 11, color: INK_LOW, mb: `${SP[1]}px` }}>
                    예보 없음
                  </Typography>
                )
              )}
              {/* [목적] Day 합계 예상 비용 — 값이 있을 때만 노출
                  [이유] 2026-09-18 QA(M-2): 생성 파이프라인이 PlanItem.costEstimate 를 채우지 못하면
                         day.totalCostEstimate 가 0 이 되어 "예상 비용 ₩0" 이 찍히는데, 같은 화면 아래
                         예산 분배 패널은 "인당 약 20만원" 을 보여줘 서로 모순됐다. 0 은 "무료"가 아니라
                         "데이터 없음"이므로 문구 자체를 감춘다.
                  [사이드 이팩트] 실제로 전액 무료인 Day 도 이 줄이 사라진다 — 예산 분배 패널이 총액을 담당.
                  [수정 시 고려사항] 근본 해결은 BE 생성 단계에서 item 별 costEstimate 를 채우는 것.
                                     그때 이 guard 는 제거해도 된다. FolioMobile.tsx 에 동일 분기 존재. */}
              {day.totalCostEstimate > 0 && (
                <Typography sx={{ fontSize: 11.5, color: INK_MID, mb: `${SP[4]}px` }}>
                  예상 비용{' '}
                  <Box
                    component="strong"
                    sx={{ color: INK_HI, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                  >
                    ₩{day.totalCostEstimate.toLocaleString()}
                  </Box>
                </Typography>
              )}
              {/* T13 + folio-generation-quality gap: 밴드 그룹 타임라인
                  [목적] ADR-FGQ2: 하루 일정을 시간대 밴드로 묶어 매거진형 하루 서사 표현
                  [이유] groupPlanItemsByTimeBand(timeBand.ts) 재사용 — PlanItem.timeOfDay 우선(영속된 LLM 밴드),
                         없으면 startAt ISO 파생, 그것도 없으면 'afternoon' 기본값(buildTimeBand 3단계 폴백).
                  [사이드 이팩트] hover 인덱스는 day 전체 기준 유지(밴드 내 로컬 인덱스 아님)
                  [수정 시 고려사항] 빈 밴드 자동 숨김(groupPlanItemsByTimeBand 내부). */}
              {(() => {
                const bandGroups = groupPlanItemsByTimeBand(day.items);
                // day 전체 기준 절대 인덱스 추적 (hover 연동)
                let globalIdx = 0;
                return bandGroups.map(({ band, items: bandItems }) => {
                  const meta = BAND_META[band];
                  const groupStart = globalIdx;
                  globalIdx += bandItems.length;
                  return (
                    <Box
                      key={band}
                      component="section"
                      aria-label={`${meta.label} 일정`}
                      sx={{ mb: `${SP[3]}px` }}
                    >
                      {/* 밴드 헤더 */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: `${SP[2]}px`,
                          mb: `${SP[3]}px`,
                          pb: `${SP[2]}px`,
                          borderBottom: `1px solid ${BORDER_SOFT}`,
                        }}
                      >
                        {/* [수정: 디자인 개편 2026-09-20] 맨몸 이모지 대신 타일로 감싸 밴드 헤더의 시작점을 명확히 한다. */}
                        <Box
                          component="span"
                          aria-hidden="true"
                          sx={{
                            width: 26,
                            height: 26,
                            flexShrink: 0,
                            borderRadius: `${R.s}px`,
                            bgcolor: PAPER2,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            lineHeight: 1,
                          }}
                        >
                          {meta.emoji}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 800,
                            color: INK_HI,
                            letterSpacing: '0.3px',
                          }}
                        >
                          {meta.label}
                        </Typography>
                      </Box>

                      {/* 밴드 내 아이템 */}
                      {bandItems.map((item, localIdx) => {
                        const i = groupStart + localIdx;
                        const isHover = hover === i;
                        const isLast = localIdx === bandItems.length - 1;
                        // [목적] 타임라인 좌측 레일에 "순번" 대신 "시각"을 표시
                        // [이유] 2026-09-20 디자인 QA: 시간순 일정인데 레일은 1·2·3 순번만 보여줘 "언제"가
                        //        빠져 있었다. 순번은 본문 순서로 이미 알 수 있고, 시각이 훨씬 고관여도 높다.
                        //        기존에는 같은 시각을 카드 본문 상단 칩으로 보여줘 정보가 흔어져 있었다 —
                        //        레일로 옮기고 본문 칩은 제거해 한 곳에서만 다룬다.
                        // [사이드 이팩트] startAt/scheduledTime 이 모두 null 인 기존 폴리오는 순번 뱃지로 폴백
                        //                  (현재 생성 파이프라인이 시각을 채우지 않는 케이스가 대다수라 폴백이 필수다).
                        // [수정 시 고려사항] formatTimeRange 는 "HH:MM–HH:MM" 또는 "HH:MM~" 를 리턴한다.
                        const timeLabel = formatTimeRange(item);
                        const [startLabel, endLabel]: [string | null, string | null] = timeLabel
                          ? timeLabel.includes('–')
                            ? [timeLabel.split('–')[0] ?? null, timeLabel.split('–')[1] ?? null]
                            : [timeLabel.replace('~', ''), null]
                          : [null, null];
                        // 접근성: "18:00–19:30" → "18시 0분부터 19시 30분까지" 형식
                        const hhmmToKo = (v: string | null): string => {
                          const [h, m] = (v ?? '').split(':');
                          return `${parseInt(h ?? '0', 10)}시 ${parseInt(m ?? '0', 10)}분`;
                        };
                        const timeAria = startLabel
                          ? endLabel
                            ? `${hhmmToKo(startLabel)}부터 ${hhmmToKo(endLabel)}까지`
                            : `${hhmmToKo(startLabel)}부터`
                          : `${i + 1}번째 일정`;
                        return (
                          <Box
                            key={item.id}
                            ref={(el: HTMLElement | null) => {
                              itemRefs.current[i] = el;
                            }}
                            onMouseEnter={() => setHover(i)}
                            onMouseLeave={() => setHover(null)}
                            // [목적] 타임라인 카드 클릭 시 해당 POI 의 혼잡도 카드 표시
                            // [이유] WC-TOURAPI-QR-5-03 + [G2-2, 2026-09-17]: PlanItem.poiId 는 항상 null(BE
                            //        미배선) 이라 대신 factInfo.contentId(TourAPI content_id 원문, 계약 C2)를
                            //        사용한다 — GET /pois/:id/congestion 의 :id 는 실제로 content_id 다
                            //        (poi-meta.controller.ts 주석 근거).
                            // [사이드 이팩트] selectedPoiId 변경 → usePoiCongestion 훅이 새 contentId 로 재요청.
                            // [수정 시 고려사항] factInfo 자체가 없는 구버전 데이터(DDL 19 이전)는 contentId 도
                            //                   없으므로 카드가 계속 미표시(기존 graceful 동작 유지).
                            // [2026-09-21] 혼잡도 카드 표시에 더해 지도 포커스까지 함께 이동한다.
                            //   같은 항목을 다시 누르면 선택이 풀리고 전체 동선 뷰로 돌아간다(토글).
                            onClick={() => {
                              setSelectedPoiId(item.factInfo?.contentId ?? null);
                              setFocusedIndex((prev) => (prev === i ? null : i));
                            }}
                            sx={{
                              display: 'flex',
                              gap: `${SP[3]}px`,
                              mb: `${SP[2]}px`,
                              position: 'relative',
                              cursor: 'pointer',
                            }}
                          >
                            <Stack
                              alignItems="center"
                              aria-label={timeAria}
                              sx={{
                                width: startLabel ? 52 : 42,
                                flexShrink: 0,
                                pt: `${SP[1]}px`,
                              }}
                            >
                              {/* [목적] 일정 순번 뱃지 — 타임라인 좌측 원형 번호
                                  [이유] 2026-09-18 QA(H-3): 원형 뱃지 위에 동일한 `{i + 1}` Typography 가
                                         한 번 더 렌더돼 "11", "22" 로 보이던 중복을 제거했다.
                                         시각 라벨은 아래 본문 영역의 formatTimeRange 가 이미 담당한다.
                                  [사이드 이팩트] 없음 — 중복 노드 1개 제거
                                  [수정 시 고려사항] 순번 표기는 이 뱃지 한 곳뿐 — 다시 추가하지 말 것 */}
                              {startLabel ? (
                                /* 시각 알약 — startAt/scheduledTime 이 있는 아이템 */
                                <Box
                                  aria-hidden="true"
                                  sx={{
                                    minWidth: 46,
                                    height: 22,
                                    px: `${SP[1]}px`,
                                    borderRadius: `${R.xl}px`,
                                    bgcolor: isHover ? BRAND : c.paper[0][L],
                                    color: isHover ? c.paper[0][L] : BRAND,
                                    border: `1px solid ${isHover ? BRAND : `${BRAND}59`}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    letterSpacing: '-0.2px',
                                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                                    transition: `all ${MO.fast.duration}ms`,
                                  }}
                                >
                                  {startLabel}
                                </Box>
                              ) : (
                                /* 시각 데이터가 없는 폴리오 폴백 — 기존 순번 뱃지 */
                                <Box
                                  aria-hidden="true"
                                  sx={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    bgcolor: isHover ? BRAND : c.paper[0][L],
                                    color: isHover ? c.paper[0][L] : BRAND,
                                    border: `2px solid ${BRAND}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 11,
                                    fontWeight: 800,
                                    lineHeight: 1,
                                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                                    transition: `all ${MO.fast.duration}ms`,
                                  }}
                                >
                                  {i + 1}
                                </Box>
                              )}
                              {endLabel && (
                                /* 종료 시각 — 시작 시각보다 한 단계 낮은 위계로 바로 아래에 붙인다 */
                                <Box
                                  aria-hidden="true"
                                  sx={{
                                    mt: '2px',
                                    fontSize: 9,
                                    color: INK_LOW,
                                    fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                                    letterSpacing: '-0.2px',
                                  }}
                                >
                                  {`~${endLabel}`}
                                </Box>
                              )}
                              {!isLast && (
                                <Box
                                  sx={{
                                    width: 1.5,
                                    flex: 1,
                                    bgcolor: BORDER,
                                    mt: `${SP[0]}px`,
                                    minHeight: 18,
                                  }}
                                />
                              )}
                            </Stack>
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                bgcolor: isHover ? `${BRAND}0A` : c.paper[0][L],
                                // [수정: 디자인 개편 2026-09-20] 12px 패딩은 한글 본문이 들어가기엔 빡빡해 글이 바짝 붙어
                                // 보였다. 카드 안쪽 여백을 늘리고 라운드를 키워 매거진형 카드 느낌을 맞춘다.
                                borderRadius: '14px',
                                p: `${SP[4]}px`,
                                border: `1px solid ${isHover ? BRAND : BORDER}`,
                                boxShadow: isHover ? `0 8px 22px -10px ${INK_HI}38` : 'none',
                                transition: `all ${MO.fast.duration}ms`,
                              }}
                            >
                              <Stack direction="row" spacing={`${SP[2]}px`} alignItems="flex-start">
                                <Box
                                  aria-hidden="true"
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: `${R.s}px`,
                                    bgcolor: PAPER2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 17,
                                    flexShrink: 0,
                                  }}
                                >
                                  {getItemEmoji(item)}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  {/* [수정: 디자인 개편 2026-09-20] folio-item-time 시각 칩을 여기서 제거했다.
                                      동일한 시각을 타임라인 좌측 레일(시각 알약)이 담당하므로 같은 카드 안에 두 번
                                      노출되던 중복을 없앤다. 접근성 aria-label 도 레일 Stack 으로 이관됐다.
                                      시각을 다시 카드 본문으로 옮기지 말 것 — 레일과 중복된다. */}
                                  <Typography
                                    sx={{
                                      // [수정: 디자인 개편 2026-09-20] 장소명은 카드의 주인공인데 12.5px 라
                                      // 바로 아래 11px 설명과 구분이 거의 안 됐다.
                                      fontSize: 15,
                                      fontWeight: 700,
                                      color: INK_HI,
                                      letterSpacing: '-0.3px',
                                      lineHeight: 1.4,
                                      mb: `${SP[1]}px`,
                                    }}
                                  >
                                    {item.title ?? '(이름 없음)'}
                                  </Typography>
                                  {item.note && (
                                    <Typography
                                      sx={{
                                        // [수정: 디자인 개편 2026-09-20] 11px/1.45 는 한글 산문에 너무 빡빡하다
                                        // — 본문 가독성 기준으로 크기·행간을 올린다.
                                        fontSize: 12.5,
                                        color: INK_MID,
                                        mb: `${SP[2]}px`,
                                        lineHeight: 1.75,
                                      }}
                                    >
                                      {item.note}
                                    </Typography>
                                  )}
                                  {/* folio-enrichment WC-ENRICH-15: activity/tip/factInfo/firstImage 풍부화 렌더
                                      [목적] PlanItem 풍부화 필드를 note·비용 사이에 삽입
                                      [이유] 기존 poiName/note/cost 순서를 유지하며 풍부화 정보를 추가 (spec §5 화면)
                                      [사이드 이팩트] 모두 null/undefined 이면 DOM 노드 0 — 기존 레이아웃 무변경
                                      [수정 시 고려사항] compact=false(뷰어) — addr chip 포함 전체 표시 */}
                                  <PlanItemEnrichedContent
                                    item={item}
                                    compact={false}
                                    sx={{ mt: `${SP[1]}px`, mb: `${SP[1]}px` }}
                                  />
                                  {item.costEstimate > 0 ? (
                                    <Typography
                                      sx={{
                                        fontSize: 11,
                                        color: BRAND,
                                        fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                                        fontWeight: 700,
                                      }}
                                    >
                                      ₩{item.costEstimate.toLocaleString()}
                                    </Typography>
                                  ) : (
                                    /* [수정: 디자인 개편 2026-09-20] 맨텍스트 "무료"가 카드 폭 전체를 차지하는
                                       블록 행으로 떠 있어 본문의 일부처럼 읽혔다 — 비용 표시는 배지로 묶는다. */
                                    <Typography
                                      component="span"
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        mt: `${SP[2]}px`,
                                        px: `${SP[2]}px`,
                                        py: '3px',
                                        borderRadius: `${R.xl}px`,
                                        bgcolor: `${ACCENT}1A`,
                                        fontSize: 10.5,
                                        lineHeight: 1.4,
                                        color: ACCENT,
                                        fontWeight: 700,
                                      }}
                                    >
                                      무료
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                });
              })()}

              {/* 예산 카테고리 바 — fe-be-gap-tier2 T14: budgetBreakdown 실데이터 우선, 부재 시 BUDGET 모크 폴백 */}
              <BudgetCategoryBar
                budgetPerPerson={folio.budgetPerPerson}
                budgetBreakdown={folio.budgetBreakdown}
              />

              {/* 혼잡도 예측 카드 — 타임라인 카드 클릭 시 해당 POI 표시
                  [목적] 선택된 POI 의 30일 혼잡도 예측 곡선을 폴리오 일정 하단에 표시
                  [이유] WC-TOURAPI-QR-5-03 — usePoiCongestion 데이터가 준비된 경우에만 렌더
                  [사이드 이팩트] selectedPoiId 변경 시 usePoiCongestion 재요청 발생
                  [수정 시 고려사항] item.poiId 미포함 시 selectedPoiId=null → 카드 미표시 (정상) */}
              {congestion.data && selectedPoiId && (
                <CongestionForecastCard poiId={selectedPoiId} forecast={congestion.data} />
              )}
            </Box>
          )}

          {/* 댓글 섹션 — fe-folio-social-wire: Day 선택과 무관하게 항상 표시 */}
          <Box sx={{ px: `${SP[5]}px`, pb: `${SP[8]}px` }}>
            <FolioCommentSection folioId={folio.id} />
          </Box>
        </Box>

        {/* RIGHT: 지도 — FlowLine A4: FolioMap(SDK/폴백/빈 상태) */}
        {splitMode && day && (
          <Box sx={{ flex: 1, p: `${SP[4]}px` }}>
            {/* [2026-09-21] 확정 선택(focusedIndex)이 있으면 그것을, 없으면 hover 를 넘긴다 —
                클릭으로 고른 장소가 마우스를 떼도 지도에서 풀리지 않게 한다. */}
            <FolioMap
              day={day}
              focusedIndex={focusedIndex ?? hover}
              onFocusItem={handleFocusItem}
            />
          </Box>
        )}
      </Box>

      {/* Fork 실패 Snackbar */}
      <Snackbar
        open={forkError}
        autoHideDuration={4000}
        onClose={() => setForkError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setForkError(false)} sx={{ width: '100%' }}>
          Fork 에 실패했습니다. 잠시 후 다시 시도해 주세요.
        </Alert>
      </Snackbar>

      {/* FlowLine A7: 폴리오 신고 다이얼로그 */}
      {folioReportOpen && (
        <ReportDialog
          targetType="folio"
          targetId={folio.id}
          open
          onClose={() => setFolioReportOpen(false)}
        />
      )}
      {/* FlowLine A7: 폴리오 신고 게스트 가드 모달 */}
      <LoginPromptModal
        open={folioReportGate.isPromptOpen}
        intent={folioReportGate.pendingIntent}
        onClose={folioReportGate.closePrompt}
      />
      {/* FlowLine A8: Day 가져오기 모달 — 현재 활성 Day 기준 */}
      {day && (
        <DayImportDialog
          open={dayImportOpen}
          onClose={() => setDayImportOpen(false)}
          sourceFolioId={folio.id}
          sourceDayIndex={day.dayIndex}
        />
      )}
    </>
  );
}

// ─── StateMessage ──────────────────────────────────────────────────────────

/**
 * [목적] 로딩·에러·비공개 상태 공통 메시지 컴포넌트
 * [이유] WC-FE-FOLIO-01 — anon/private/404 분기 상태 표시
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] 없음
 */
function StateMessage({ title, body }: { title: string; body?: string }): JSX.Element {
  const { viewport, openSidebar } = useShell();
  return (
    <>
      <TopBar title="폴리오" mobile={viewport.isMobile} onBurger={openSidebar} />
      <Box
        sx={{
          p: `${SP[10]}px ${SP[8]}px`,
          textAlign: 'center',
          bgcolor: PAPER1,
          minHeight: 'calc(100vh - 54px)',
        }}
      >
        <Typography
          component="h2"
          sx={{ fontSize: 18, fontWeight: 800, color: INK_HI, mb: `${SP[2]}px` }}
        >
          {title}
        </Typography>
        {body && <Typography sx={{ fontSize: 13, color: INK_MID }}>{body}</Typography>}
      </Box>
    </>
  );
}

// ─── FolioPage ─────────────────────────────────────────────────────────────

/**
 * [목적] /folio/:id 라우트 컴포넌트 — useFolio 조회 + anon/private/error 분기
 * [이유] WC-FE-FOLIO-01 — 401(anon)/404(private)/기타 에러 분기 처리
 * [사이드 이팩트] 없음
 * [수정 시 고려사항] 401 은 anon 사용자가 비공개 folio 접근 시. 404 는 비공개·삭제된 folio.
 *                   ApiError 는 api/http.ts 에서 throw
 */
export function FolioPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: folio, isLoading, isError, error } = useFolio(id);

  if (isLoading) return <StateMessage title="폴리오 로딩 중…" />;

  if (isError || !folio) {
    const apiErr = error as { status?: number } | undefined;
    if (apiErr?.status === 401) {
      return (
        <StateMessage title="로그인이 필요합니다" body="이 폴리오를 보려면 로그인해 주세요." />
      );
    }
    if (apiErr?.status === 404) {
      return (
        <StateMessage title="폴리오를 찾을 수 없습니다" body="비공개이거나 삭제된 폴리오입니다." />
      );
    }
    return (
      <StateMessage
        title="폴리오를 불러올 수 없습니다"
        body={(error as Error | undefined)?.message ?? '알 수 없는 오류'}
      />
    );
  }

  return <FolioRouter folio={folio} />;
}

// [목적] 뷰포트에 따라 모바일/데스크톱 레이아웃 라우팅
// [이유] PWA 반응형 분기 — isMobile 시 FolioMobile, 그 외 FolioContent
// [사이드 이팩트] 없음
// [수정 시 고려사항] FolioMobile 도 동일 folio prop 을 받으므로 데이터 구조 변경 시 함께 수정
function FolioRouter({ folio }: { folio: Folio }): JSX.Element {
  const { viewport } = useShell();
  return viewport.isMobile ? <FolioMobile folio={folio} /> : <FolioContent folio={folio} />;
}
